import axios from 'axios';
import { apiUrl } from '@/config/api';

const asNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const inferLegacyBaseRate = (item) => {
  const buyMargin = asNumber(item.buyMargin);
  const sellMargin = asNumber(item.sellMargin);
  const buyMarginAmount = asNumber(item.buyMarginAmount);
  const sellMarginAmount = asNumber(item.sellMarginAmount);
  const candidates = [];

  if (buyMargin > 0 && buyMarginAmount > 0) {
    candidates.push(buyMargin / buyMarginAmount);
  }

  if (sellMargin > 0 && sellMarginAmount > 0) {
    candidates.push(sellMargin / sellMarginAmount);
  }

  if (!candidates.length) return 1;

  return candidates.reduce((total, value) => total + value, 0) / candidates.length;
};

const calculateClientRates = (sourceRate, buyMargin, sellMargin) => {
  const displayRate = sourceRate > 0 ? 1 / sourceRate : 0;
  const buyMarginAmount = displayRate * buyMargin;
  const sellMarginAmount = displayRate * sellMargin;

  return {
    buy_margin_amount: Number(buyMarginAmount.toFixed(8)),
    sell_margin_amount: Number(sellMarginAmount.toFixed(8)),
    buy_rate: Number(Math.max(displayRate - buyMarginAmount, 0).toFixed(8)),
    sell_rate: Number((displayRate + sellMarginAmount).toFixed(8))
  };
};

const normalizePublicRate = (rate) => {
  const sourceRate = asNumber(rate.source_rate) || asNumber(rate.base_rate);
  const buyMargin = asNumber(rate.buy_margin) || 0;
  const sellMargin = asNumber(rate.sell_margin) || 0;

  if (!sourceRate) return rate;

  return {
    ...rate,
    ...calculateClientRates(sourceRate, buyMargin, sellMargin)
  };
};

const mapLegacyRates = (config) => {
  const items = config?.devises?.currenciesItems || {};

  return Object.entries(items)
    .map(([currencyCode, item], index) => {
      const baseRate = inferLegacyBaseRate(item);
      const buyMargin = asNumber(item.buyMargin) || 0;
      const sellMargin = asNumber(item.sellMargin) || 0;
      const clientRates = calculateClientRates(baseRate, buyMargin, sellMargin);

      return {
        id: `legacy-${currencyCode}`,
        currency_code: currencyCode,
        currency_name: item.country_name_fr || item.country_name_en || currencyCode,
        base_rate: baseRate,
        source_rate: baseRate,
        buy_margin: buyMargin,
        sell_margin: sellMargin,
        ...clientRates,
        active: Boolean(item.home || item.principale || item.autre),
        display_order: item.principale ? index : index + 1000,
        updated_at: null
      };
    })
    .filter((rate) => rate.active)
    .sort((a, b) => {
      if (a.display_order === b.display_order) {
        return a.currency_code.localeCompare(b.currency_code);
      }
      return a.display_order - b.display_order;
    });
};

const fetchLegacyRates = async () => {
  const response = await axios.get(apiUrl('/page?name=devises_taux'));
  const rawConfig = response.data?.config;
  const config = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;

  return mapLegacyRates(config);
};

export const fetchPublicRates = async () => {
  try {
    const response = await axios.get(apiUrl('/rates'));
    const rates = response.data?.rates || [];

    if (rates.length) return rates.map(normalizePublicRate);
  } catch (error) {
    console.error('Unable to load public rates, using legacy fallback.', error);
  }

  return fetchLegacyRates();
};

export const fetchBoardRates = async () => {
  try {
    const response = await axios.get(apiUrl('/board/rates'));
    const rates = response.data?.rates || [];

    if (rates.length) {
      return {
        updated_at: response.data?.updated_at || null,
        rates
      };
    }
  } catch (error) {
    console.error('Unable to load board rates, using legacy fallback.', error);
  }

  const legacyRates = await fetchLegacyRates();

  return {
    updated_at: new Date().toISOString(),
    rates: legacyRates.slice(0, 8).map((rate) => ({
      code: rate.currency_code,
      name: rate.currency_name,
      buy: rate.buy_rate,
      sell: rate.sell_rate,
      order: rate.display_order,
      updated_at: rate.updated_at
    }))
  };
};
